<template>
  <VCollapsible v-if="doesUserManageCompany(deck.company_id) && userHasCompanies" @collapse="bubbleCollapse">
    <div class="bg-black text-white px-4 py-3 text-sm flex justify-between gap-2">
      <div class="flex items-center">
        <a class="text-white hover_text-white" :href="logoLink">
          <VLogo class="h-6 sm_hidden" />
          <VrifyLogo class="h-5 mr-6 hidden sm_block" />
        </a>

        Admin Console
      </div>

      <div class="flex gap-4">
        <!-- z-index needs to be greater than FlyoutMenu -->
        <VTooltip direction="bottom" class="z-70">
          <template #content><div class="w-52">Share a link for guests to follow along. Best used with Zoom, Teams, etc</div></template>
          <VButton data-testid="live-share" class="hidden sm_flex items-center gap-2" size="xs" @click="startLiveShare">
            <FontAwesomeIcon icon="bolt" size="sm" />
            Live Share
          </VButton>
        </VTooltip>

        <VButton data-testid="create-video-recording" class="hidden lg_flex items-center gap-2" size="xs" @click="createVideoRecording">
          <Record class="w-4" />
          Record
        </VButton>

        <FlyoutMenu menu-class="w-56 -left-48 text-sm text-gray-800">
          <template #button>
            <div
              data-testid="admin-console-menu"
              class="w-7.5 h-7.5 rounded-full bg-white border border-gray-300 flex items-center justify-center"
            >
              <Dots class="text-gray-900 w-4" />
            </div>
          </template>
          <li>
            <button
              data-testid="live-share"
              type="button"
              class="sm_hidden appearance-none bg-transparent w-full p-2 text-left flex gap-2 items-center hover_text-gray-600"
              @click="startLiveShare"
            >
              <FontAwesomeIcon icon="bolt" size="sm" />
              Live Share
            </button>
          </li>
          <li>
            <button
              data-testid="create-video-recording"
              type="button"
              class="lg_hidden appearance-none bg-transparent w-full p-2 text-left flex gap-2 items-center hover_text-gray-600"
              @click="createVideoRecording"
            >
              <Record class="w-4 -ml-0.5 -mr-0.5" />
              Record
            </button>
          </li>
          <li>
            <button
              data-testid="auto-play"
              type="button"
              class="appearance-none bg-transparent w-full p-2 text-left flex gap-2 items-center hover_text-gray-600"
              @click="$emit('start-auto-play')"
            >
              <FontAwesomeIcon icon="circle-play" size="sm" />
              Autoplay
            </button>
          </li>
        </FlyoutMenu>

        <FlyoutMenu v-if="userState.user" menu-class="w-32 -left-20 text-sm text-gray-800">
          <template #button>
            <div class="w-7.5 h-7.5 rounded-full bg-gold-700 flex items-center justify-center text-sm text-white hover_text-white">
              {{ userState.user.initials }}
            </div>
          </template>

          <li>
            <a
              href="/user/profile"
              class="appearance-none bg-transparent w-full p-2 text-left text-gray-800 flex gap-2 items-center hover_text-gray-600"
            >
              My Profile
            </a>
          </li>
          <li>
            <button
              type="button"
              class="appearance-none bg-transparent w-full p-2 text-left text-gray-800 flex gap-2 items-center hover_text-gray-600"
              @click="logout"
            >
              Log Out
            </button>
          </li>
        </FlyoutMenu>
      </div>
    </div>
  </VCollapsible>
</template>

<script lang="ts" setup>
import VLogo from '~/assets/images/mark-logo.svg'
import VrifyLogo from '~/assets/images/vrify-logo.svg'
import Dots from '@/components/icons/Dots.svg'
import Record from '@/components/icons/Record.svg'
import type { Deck, IncludeCompany } from '@/entities/deck'
import type { DeepReadonly } from 'vue'

const props = defineProps({
  deck: {
    type: Object as PropType<DeepReadonly<Deck & IncludeCompany>>,
    required: true,
  },
})

const emit = defineEmits(['start-auto-play', 'collapse', 'start-recording'])
const { $analytics, $sentry } = useNuxtApp()

const config = useRuntimeConfig()
const logoLink = config.public.ADMIN_ENDPOINT

const { state: userState, setSelectedCompany, userHasCompanies, doesUserManageCompany, logout } = useUser()
const { createMeeting, createRecording } = useMeetings({ userState, setSelectedCompany })

const collapsed = ref(false)
function bubbleCollapse(value: boolean) {
  collapsed.value = value
  emit('collapse', value)
}

async function startLiveShare() {
  try {
    const { share_id } = await createMeeting({
      title: 'Instant Meeting',
      companyId: props.deck.company_id,
      deck_id: props.deck.id,
      is_caption_enabled: true,
      is_audio_video_enabled: false,
    })

    navigateTo({ name: 'meeting-id', params: { id: share_id }, query: { skipLobby: 'true', from: window.location.href } })
  } catch (e) {
    $sentry?.captureException(e)
  }
}

const userFlaggedForRecordingTool = computed(() => userState.value.flags.DECK_RECORDING_ENABLED === true)

async function createVideoRecording() {
  try {
    $analytics.track('DeckClickCreateVideoRecording', {
      companyName: props.deck.company?.name,
      companyId: props.deck.company_id,
      deckName: props.deck.title,
      deckId: props.deck.id,
    })

    if (userFlaggedForRecordingTool.value) {
      emit('start-recording')
    } else {
      const { share_id } = await createRecording({
        title: `${props.deck.title} - ${props.deck.company?.name}`,
        deckId: props.deck.id,
        companyId: props.deck.company_id,
      })

      navigateTo({ name: 'recordings-id', params: { id: share_id }, query: { from: window.location.href } })
    }
  } catch (e) {
    $sentry?.captureException(e)
  }
}
</script>
